import {
  Box,
  CircularProgress,
  CssBaseline,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import { ReactElement, useEffect } from 'react';
import { NavLink, Route, Routes, useParams } from 'react-router-dom';
import { useAuth, useCompany } from '../stores';
import Main from '../views/Company/Main';
import Receipts from '../views/Company/Receipts';
import ProductsView from '../views/Company/Products';
import Subscriptions from '../views/Company/Subscriptions';
import CashRegister from '../views/Company/CashRegister';
import BookkeepingView from '../views/Company/Bookkeeping';
import PaymentMethodsList from '../views/Company/PaymentMethodsList';
import DeviceList from '../views/Company/DeviceList';
import PrinterList from '../views/Company/Printers';
import Giftcards from '../views/Company/Giftcards/Giftcards';
import GlobalAlert from '../components/GlobalAlert';
import ProductImport from '../views/Company/ProductImport';
import Integrations from '../views/Company/integrations/Integrations';
import Users from '../views/Company/Users';
import Invoicing from '../views/Company/invoicing/Invoicing';

const DRAWER_WIDTH = 200;

const CompanyRouter = observer((): ReactElement => {
  const companyStore = useCompany();
  const auth = useAuth();
  const params = useParams() as { companyKey: string };
  const { companyKey } = params;

  useEffect(() => {
    if (companyKey) {
      companyStore.setCompany(companyKey);
    }
  }, [companyKey]);

  if (companyStore.loading) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          translate: '-50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress color="info" sx={{ marginBottom: '1rem' }} />
        <Typography variant="subtitle1">Loading company...</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          display: { xs: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <NavLink to="" className="textDecorationNone">
            <Typography variant="h5" sx={{ p: 2 }}>
              {companyStore.data?.name}
            </Typography>
          </NavLink>
          <List component="nav" aria-label="main mailbox folders">
            <NavLink to="invoicing" className="textDecorationNone">
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Fakturering" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="integrations" className="textDecorationNone">
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Integrationer" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="receipts" className="textDecorationNone">
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Kvitton" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="products" className="textDecorationNone">
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Artiklar" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="bookkeeping" className="textDecorationNone">
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Bokföring" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="cashregister" style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Kassaregister" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="paymethod" style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Betalmetod" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="printers" style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Skrivare" />
                </ListItemButton>
              )}
            </NavLink>
            <NavLink to="giftcards" style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Presentkort" />
                </ListItemButton>
              )}
            </NavLink>
            {auth.isAdmin && (
              <NavLink to="users" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <ListItemButton selected={isActive}>
                    <ListItemText primary="Users" />
                  </ListItemButton>
                )}
              </NavLink>
            )}
            {auth.isAdmin && (
              <NavLink to="deviceList" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <ListItemButton selected={isActive}>
                    <ListItemText primary="Enheter" />
                  </ListItemButton>
                )}
              </NavLink>
            )}
            <NavLink to="productImport" style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <ListItemButton selected={isActive}>
                  <ListItemText primary="Produkt import (Beta)" />
                </ListItemButton>
              )}
            </NavLink>
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, marginLeft: `${DRAWER_WIDTH}px` }}>
        <GlobalAlert />
        <Routes>
          <Route path="subscriptions" element={<Subscriptions />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="receipts" element={<Receipts />} />
          <Route path="bookkeeping" element={<BookkeepingView />} />
          <Route path="invoicing" element={<Invoicing />} />
          <Route path="products" element={<ProductsView />} />
          <Route path="cashregister" element={<CashRegister />} />
          <Route path="paymethod" element={<PaymentMethodsList />} />
          <Route path="users" element={<Users />} />
          <Route path="deviceList" element={<DeviceList />} />
          <Route path="printers" element={<PrinterList />} />
          <Route path="giftcards" element={<Giftcards />} />
          <Route path="productImport" element={<ProductImport />} />
          <Route path="*" element={<Main />} />
        </Routes>
      </Box>
    </Box>
  );
});

export default CompanyRouter;
